import { Container, Graphics, Sprite, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';

import { HORSE_IDLE } from '../art/sprites/horse';
import { DIRT_TILE, FENCE, GRASS_TILE, TREE_SMALL } from '../art/sprites/scenery';
import { bindKeys } from '../input/InputController';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { xpFor } from '../sim/minigames/framework';
import { PenSession, type SpookKind } from '../sim/minigames/pen';
import { el } from '../ui/dom';
import {
  InputCollector,
  SessionRunner,
  launchPalette,
  showTrainingResults,
  trainingControls,
  type TrainingLaunch,
  type TrainingSceneOptions,
} from './minigameCommon';
import type { Scene, SceneContext } from './SceneManager';
import { drawSpookObject } from './trainingGraphics';

const CENTER_X = 240;
const SWAY_PX = 130;
const HORSE_Y = 204;

const SPOOK_LABEL: Record<SpookKind, string> = {
  tarp: 'flapping tarp!',
  goose: 'loose goose!',
  umbrella: 'umbrella!',
  bucket: 'dropped bucket!',
};

/** Heart training: spooks pull her off line — steer her back to center. */
export class PenScene implements Scene {
  private readonly launch: TrainingLaunch;
  private readonly options: TrainingSceneOptions;
  private ctx!: SceneContext;
  private session!: PenSession;
  private runner!: SessionRunner;
  private collector!: InputCollector;
  private container!: Container;
  private horse!: Sprite;
  private idleFrames!: Texture[];
  private spookLeft!: Graphics;
  private spookRight!: Graphics;
  private corridor!: Graphics;
  private hud!: HTMLElement;
  private calmFill!: HTMLElement;
  private calmWrap!: HTMLElement;
  private timeText!: HTMLElement;
  private cleanText!: HTMLElement;
  private flash!: HTMLElement;
  private unbind: (() => void) | null = null;
  private resultsShown = false;
  private disposeResults: (() => void) | null = null;

  constructor(launch: TrainingLaunch, options: TrainingSceneOptions) {
    this.launch = launch;
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.session = new PenSession(this.launch.tier, this.launch.seed, this.launch.mods);
    this.runner = new SessionRunner();
    this.collector = new InputCollector({ steer: true });

    this.container = new Container();
    ctx.scaler.root.addChild(this.container);
    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 48)
      .fill(0x87a8c8)
      .rect(0, 48, VIRTUAL_W, 12)
      .fill(0x6f94b8);
    this.container.addChild(sky);
    const grass = new TilingSprite({
      texture: bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: VIRTUAL_H - 60,
    });
    grass.position.set(0, 60);
    this.container.addChild(grass);
    for (const tx of [40, 440]) {
      const tree = new Sprite(bakeFrames(TREE_SMALL.def, TREE_SMALL.palette)[0]!);
      tree.position.set(tx - 11, 108 - TREE_SMALL.def.h);
      this.container.addChild(tree);
    }
    // The pen: a wide dirt ring between two fence rails.
    const penDirt = new TilingSprite({
      texture: bakeFrames(DIRT_TILE.def, DIRT_TILE.palette)[0]!,
      width: 400,
      height: 108,
    });
    penDirt.position.set(40, 120);
    this.container.addChild(penDirt);
    const fenceTex = bakeFrames(FENCE.def, FENCE.palette)[0]!;
    const backRail = new TilingSprite({ texture: fenceTex, width: 400, height: FENCE.def.h });
    backRail.position.set(40, 120 - FENCE.def.h + 4);
    this.container.addChild(backRail);

    this.corridor = new Graphics();
    const halfPx = this.session.config.corridor * SWAY_PX;
    for (let y = 128; y < 222; y += 8) {
      this.corridor
        .rect(CENTER_X - halfPx, y, 2, 4)
        .fill({ color: 0xefe8d8, alpha: 0.4 })
        .rect(CENTER_X + halfPx, y, 2, 4)
        .fill({ color: 0xefe8d8, alpha: 0.4 });
    }
    this.container.addChild(this.corridor);

    this.spookLeft = new Graphics();
    this.spookRight = new Graphics();
    this.container.addChild(this.spookLeft, this.spookRight);

    this.idleFrames = bakeFrames(HORSE_IDLE, launchPalette(this.launch));
    this.horse = new Sprite(this.idleFrames[0]!);
    this.container.addChild(this.horse);

    const frontRail = new TilingSprite({ texture: fenceTex, width: 440, height: FENCE.def.h });
    frontRail.position.set(20, 236);
    this.container.addChild(frontRail);

    this.calmFill = el('div', { class: 'stamina-fill' });
    this.calmWrap = el('div', { class: 'stamina-wrap calm' }, this.calmFill);
    this.timeText = el('span', { class: 'hud-chip' });
    this.cleanText = el('span', { class: 'hud-chip' });
    this.flash = el('div', { class: 'race-flash' });
    this.hud = el(
      'div',
      { class: 'drill-hud with-training-controls' },
      el('div', { class: 'ranch-topbar' }, this.timeText, this.cleanText),
      this.flash,
      el(
        'div',
        { class: 'race-bottom' },
        el('div', { class: 'stamina-label' }, 'CALM'),
        this.calmWrap,
      ),
      trainingControls(this.collector, [
        { action: 'left', mode: 'hold', label: '← LEFT' },
        { action: 'right', mode: 'hold', label: 'RIGHT →' },
      ]),
      el('div', { class: 'ranch-hints' }, 'Hold ←/→, A/D, or the reins to counter the pull and stay between the guide lines'),
    );
    ctx.uiRoot.append(this.hud);

    this.unbind = bindKeys([{ code: 'Escape', handler: () => this.endEarly() }]);
  }

  frame(nowMs: number): void {
    this.runner.advance(nowMs, this.session, this.collector);
    for (const event of this.session.drainEvents()) {
      switch (event.kind) {
        case 'spook':
          this.showFlash(SPOOK_LABEL[event.spook], 'go');
          break;
        case 'clean':
          this.showFlash(event.streak >= 3 ? `CLEAN ×${event.streak} — BOND!` : 'CLEAN PASS', 'good');
          break;
        case 'breached':
          this.showFlash('TOO CLOSE!', 'bad');
          break;
        case 'bolt':
          this.showFlash('SHE BOLTS!', 'bad');
          break;
        default:
          break;
      }
    }
    this.draw(nowMs);
    if (this.session.finished && !this.resultsShown) this.showResults();
  }

  private draw(nowMs: number): void {
    const session = this.session;
    const x = CENTER_X + session.p * SWAY_PX;
    this.horse.texture = this.idleFrames[Math.floor((nowMs / 1000) * 2) % this.idleFrames.length]!;
    this.horse.position.set(Math.round(x) - HORSE_IDLE.anchor[0], HORSE_Y - HORSE_IDLE.h);

    const active = session.activeSpooks();
    const leftActive = active.find((s) => s.side === -1);
    const rightActive = active.find((s) => s.side === 1);
    const jitter = Math.round(Math.sin(nowMs / 40) * 2);
    this.spookLeft.clear();
    this.spookRight.clear();
    if (leftActive) drawSpookObject(this.spookLeft, leftActive.kind, 74 + jitter, 218, true);
    if (rightActive) drawSpookObject(this.spookRight, rightActive.kind, 406 + jitter, 218);

    this.calmFill.style.width = `${(session.calm * 100).toFixed(1)}%`;
    this.calmWrap.classList.toggle('low', session.calm < 0.35);
    const timeLeft = Math.max(0, (session.durationTicks - session.tick) / 30);
    this.timeText.textContent = `⏱ ${timeLeft.toFixed(1)}s`;
    this.cleanText.textContent = `clean ${session.cleanPasses}/${session.spooks.length}`;
  }

  private endEarly(): void {
    if (this.session.finished) return;
    this.runner.fastForward(this.session);
  }

  private showFlash(text: string, tone: 'go' | 'good' | 'bad'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash';
    void this.flash.offsetWidth;
    this.flash.className = `race-flash show ${tone}`;
  }

  private showResults(): void {
    this.resultsShown = true;
    const score = this.session.score();
    const xp = xpFor(score.raw, this.launch.xpMult);
    const finish = (): void => {
      const tape = [...this.runner.tape];
      this.ctx.scenes.pop();
      this.options.onDone(tape);
    };
    this.disposeResults = showTrainingResults(
      this.ctx.uiRoot,
      { title: 'Round pen done', detail: score.detail, xp, failed: score.failed },
      finish,
    );
    this.unbind?.();
    this.unbind = bindKeys([
      { code: 'Enter', inModal: true, handler: finish },
      { code: 'Escape', inModal: true, handler: finish },
    ]);
  }

  exit(): void {
    this.unbind?.();
    this.collector.dispose();
    this.disposeResults?.();
    this.hud.remove();
    this.container.destroy({ children: true });
  }
}
